import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../lib/api";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";
import { Crumbs } from "../components/Crumbs";
import { DocumentsPanel } from "../components/DocumentsPanel";

import { SharePanel } from "../components/SharePanel";
import { ShortlistPanel } from "../components/ShortlistPanel";
import { ThreadPanel } from "../components/ThreadPanel";
import { TreatmentDialog } from "../components/TreatmentDialog";
import { TimelinePanel } from "../components/TimelinePanel";

export function ProjectRoute() {
  const { spaceId = "", projectId = "" } = useParams();
  const queryClient = useQueryClient();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // Shares a cache entry with Crumbs and SpaceRoute, so this costs nothing
  // extra when arriving from either.
  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const project = useQuery({
    queryKey: ["project", spaceId, projectId],
    queryFn: () => api.getProject(spaceId as never, projectId as never),
  });

  const documentsKey = ["documents", spaceId, projectId];
  const documents = useQuery({
    queryKey: documentsKey,
    queryFn: () => api.listDocuments(spaceId as never, projectId as never),
    enabled: project.isSuccess,
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["project", spaceId, projectId] }),
      queryClient.invalidateQueries({ queryKey: ["projects", spaceId] }),
    ]);

  const rename = useMutation({
    mutationFn: (name: string) => api.updateProject(spaceId as never, projectId as never, { name }),
    onSuccess: async () => {
      setEditingName(false);
      await invalidate();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: "active" | "archived") =>
      api.updateProject(spaceId as never, projectId as never, { status }),
    onSuccess: () => invalidate(),
  });

  const [treatmentOpen, setTreatmentOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [conflict, setConflict] = useState<string | null>(null);

  const treatment = documents.data?.find((document) => document.id === project.data?.treatmentId);

  const save = useMutation({
    mutationFn: () => {
      if (!treatment) throw new Error("no treatment to save");
      return api.updateDocument(spaceId as never, projectId as never, treatment.id, {
        title: title.trim(),
        body,
        kind: treatment.kind,
        baseRevision: treatment.revision,
      });
    },
    onSuccess: async () => {
      setEditing(false);
      setConflict(null);
      await queryClient.invalidateQueries({ queryKey: documentsKey });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isConflict) {
        // Somebody else's edit landed first. The only correct answer is to
        // re-read and reconcile, not to retry with the same base revision
        // and overwrite exactly what the check was protecting (ADR 000027).
        setEditing(false);
        setConflict(
          "Somebody else changed the treatment while you were editing. Reloaded it; your edit was not saved.",
        );
        void queryClient.invalidateQueries({ queryKey: documentsKey });
      }
    },
  });

  if (project.isPending) return <p role="status">Loading…</p>;

  if (project.error) {
    const missing = project.error instanceof ApiError && project.error.isNotFound;
    return (
      <section className="empty">
        <h1>{missing ? "No such project" : "Could not load this project"}</h1>
        <p className="muted">{missing ? "It is not in this space." : project.error.message}</p>
        <p>
          <Link to={`/spaces/${spaceId}`}>Back to the space</Link>
        </p>
      </section>
    );
  }

  const archived = project.data.status === "archived";
  // Matches SpaceRoute: a viewer can read a project but not change it, which
  // is the same line project creation draws (ADR 000036).
  const mayEdit = space.data ? space.data.role !== "viewer" : false;

  function startEditing() {
    if (!treatment) return;
    setTitle(treatment.title);
    setBody(treatment.body);
    setConflict(null);
    setEditing(true);
  }

  return (
    <section>
      <header className="page-head">
        <Crumbs spaceId={spaceId} />

        {editingName ? (
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault();
              if (nameDraft.trim() && nameDraft.trim() !== project.data.name) {
                rename.mutate(nameDraft.trim());
              } else {
                setEditingName(false);
              }
            }}
          >
            <input
              aria-label="Project name"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              maxLength={300}
              autoFocus
            />
            <button type="submit" disabled={!nameDraft.trim() || rename.isPending}>
              {rename.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className="ghost" onClick={() => setEditingName(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="section-bar">
            <h1>{project.data.name}</h1>
            {mayEdit && (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setNameDraft(project.data.name);
                  setEditingName(true);
                }}
              >
                Rename
              </button>
            )}
          </div>
        )}

        {rename.error && (
          <p role="alert" className="error">
            {rename.error instanceof ApiError ? rename.error.userMessage : rename.error.message}
          </p>
        )}

        {/*
         * No stage here any more. It was a word somebody chose once that
         * nothing read and nothing checked, sitting next to a chain that
         * answers the same question and cannot be wrong (ADR 000064).
         */}
        <p className="muted">
          /{project.data.slug} · {project.data.format} · {project.data.status}
        </p>

        <div className="project-actions">
          <button type="button" onClick={() => setTreatmentOpen(true)}>
            Treatment
          </button>
        </div>
      </header>

      {archived && (
        <p className="muted">
          This project is archived. It keeps its treatment, timeline and notes, and is listed under
          Archived on the space page rather than in the projects above it.
        </p>
      )}

      {/*
       * The chain, first, because it is the thing being made (ADR 000064). It
       * carries its own reading of how far along the work is, which is why
       * there is no stage above it any more.
       */}
      <TimelinePanel spaceId={project.data.spaceId} projectId={project.data.id} mayEdit={mayEdit} />

      {conflict && (
        <p role="alert" className="error">
          {conflict}
        </p>
      )}

      {/*
       * Reached rather than printed. Each of these was competing with the chain
       * for the top of the page, and the chain lost: a long treatment pushed it
       * off the screen entirely.
       */}
      <details className="project-drawer">
        <summary>Documents</summary>
        <DocumentsPanel
          spaceId={project.data.spaceId}
          projectId={project.data.id}
          mayEdit={mayEdit}
        />
      </details>

      <details className="project-drawer">
        <summary>Shortlist</summary>
        <ShortlistPanel
          spaceId={project.data.spaceId}
          projectId={project.data.id}
          mayEdit={mayEdit}
        />
      </details>

      <details className="project-drawer">
        <summary>Thread</summary>
        <ThreadPanel spaceId={project.data.spaceId} projectId={project.data.id} mayEdit={mayEdit} />
      </details>

      <details className="project-drawer">
        <summary>Sharing</summary>
        <SharePanel spaceId={project.data.spaceId} projectId={project.data.id} mayEdit={mayEdit} />
      </details>

      {treatmentOpen && (
        <TreatmentDialog
          treatment={treatment}
          loading={documents.isPending}
          mayEdit={mayEdit}
          editing={editing}
          title={title}
          body={body}
          onTitle={setTitle}
          onBody={setBody}
          onEdit={startEditing}
          onCancel={() => setEditing(false)}
          onSave={() => save.mutate()}
          saving={save.isPending}
          error={
            save.isError && !(save.error instanceof ApiError && save.error.isConflict)
              ? save.error instanceof ApiError
                ? save.error.userMessage
                : save.error.message
              : null
          }
          onClose={() => {
            setTreatmentOpen(false);
            setEditing(false);
          }}
        />
      )}

      {mayEdit && (
        // A div rather than a paragraph: the remove control opens a form, and a
        // form inside a p is reparented out of it by the parser.
        <div className="danger-zone">
          <button
            type="button"
            className="ghost danger"
            disabled={setStatus.isPending}
            onClick={() => {
              if (archived) {
                setStatus.mutate("active");
                return;
              }
              // Reversible, so a confirmation rather than an undo is enough:
              // archiving only changes what is listed, nothing is removed.
              if (
                window.confirm(
                  `Archive "${project.data.name}"? It moves out of the project list into the space's Archived section, where you can put it back.`,
                )
              ) {
                setStatus.mutate("archived");
              }
            }}
          >
            {/*
             * Unarchive rather than Restore. Restore is what a project in the
             * recovery window gets, and one word for two different acts is how
             * somebody clicks the wrong one (ADR 000078, ADR 000079).
             */}
            {setStatus.isPending ? "Saving…" : archived ? "Unarchive" : "Archive"}
          </button>
          <ProjectRemoveControl
            spaceId={spaceId}
            projectId={project.data.id}
            currentName={project.data.name}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Remove a project (ADR 000078). Editor, the role that creates one.
 *
 * The same typed confirmation a space removal asks for. Archiving sits beside
 * this and is reversible with one click, so a removal that could be reached as
 * easily would be reached by mistake by somebody who meant the other one.
 *
 * The copy says what actually happens, because the question somebody is really
 * asking is whether the footage goes with it. It does not: a project references
 * assets and never owns them (ADR 000008).
 */
function ProjectRemoveControl({
  spaceId,
  projectId,
  currentName,
}: {
  spaceId: string;
  projectId: string;
  currentName: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  const remove = useMutation({
    mutationFn: () => api.removeProject(spaceId as never, projectId as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", spaceId] });
      // Off this page, because the project no longer answers on any route,
      // including this one.
      navigate(`/spaces/${spaceId}`);
    },
  });

  const confirmed = confirmation === currentName;

  if (!open) {
    return (
      <button type="button" className="ghost danger" onClick={() => setOpen(true)}>
        Remove project
      </button>
    );
  }

  return (
    <form
      className="space-remove"
      onSubmit={(event) => {
        event.preventDefault();
        if (confirmed) remove.mutate();
      }}
    >
      <p>
        Removing {currentName} takes its treatment, timeline, notes, thread and share links with it.
        The footage stays: it belongs to the space, not to this project. Nothing is destroyed today,
        and you have {RECOVERY_WINDOW_DAYS} days to put it back, whole, from the space page.
      </p>
      <input
        aria-label={`Type ${currentName} to confirm removal`}
        placeholder={`Type ${currentName} to confirm`}
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
      />
      <button type="submit" className="danger" disabled={!confirmed || remove.isPending}>
        {remove.isPending ? "Removing…" : "Remove project"}
      </button>
      <button
        type="button"
        className="ghost"
        onClick={() => {
          setOpen(false);
          setConfirmation("");
        }}
      >
        Cancel
      </button>
      {confirmation !== "" && !confirmed && (
        <p className="muted">That is not this project&rsquo;s name, so nothing has changed.</p>
      )}
      {remove.error && (
        <p role="alert" className="error">
          {remove.error instanceof ApiError ? remove.error.userMessage : remove.error.message}
        </p>
      )}
    </form>
  );
}
